import React, { useState, useEffect } from 'react';
import {
    ChevronLeft, ChevronRight, Download, Settings,
    Plus, Calendar as CalendarIcon, Check, ArrowRight,
    Users, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import api from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

const CalendarPage = () => {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [appointments, setAppointments] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());

    const setupTasks = [
        { title: t('calendar.connectCalendar'), completed: true },
        { title: t('calendar.activateReminders'), completed: true },
        { title: t('calendar.createFirstBooking'), completed: true },
        { title: t('calendar.configureServices'), completed: true },
        { title: t('calendar.addSpecialists'), completed: false },
        { title: t('calendar.createBookingPage'), completed: true },
        { title: t('calendar.activateMessageAutomation'), completed: false },
    ];

    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                const response = await api.get('/api/booking/appointments');
                if (response.ok) {
                    const data = await response.json();
                    setAppointments(data);
                }
            } catch (err) {
                console.error("Error fetching appointments:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAppointments();
    }, []);

    const getWeekDates = (date) => {
        const start = new Date(date);
        start.setDate(start.getDate() - start.getDay() + (start.getDay() === 0 ? -6 : 1));
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            return d;
        });
    };

    const weekDates = getWeekDates(currentDate);
    const dayNames = t('calendar.dayNames');

    const getAppointmentsForDay = (date) => {
        return appointments.filter(app => {
            const appDate = new Date(app.appointment_date);
            return appDate.getDate() === date.getDate() &&
                   appDate.getMonth() === date.getMonth() &&
                   appDate.getFullYear() === date.getFullYear();
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-48px)] bg-slate-50 rounded-3xl border border-slate-200">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-48px)] bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm relative">

            {/* Left Sidebar inside Calendar */}
            <div className="w-72 border-r border-slate-100 flex flex-col p-6 shrink-0">
                <h1 className="text-xl font-bold text-slate-900 mb-8 px-2">{t('calendar.title')}</h1>

                {/* Month Mini Calendar */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4 px-2">
                        <span className="font-bold text-slate-700">
                            {currentDate.toLocaleString(language,{ month: 'long', year: 'numeric' })}
                        </span>
                        <div className="flex gap-2">
                            <ChevronLeft
                                className="h-4 w-4 text-slate-400 cursor-pointer hover:text-blue-600"
                                onClick={() => {
                                    const d = new Date(currentDate);
                                    d.setMonth(d.getMonth() - 1);
                                    setCurrentDate(d);
                                }}
                            />
                            <ChevronRight
                                className="h-4 w-4 text-slate-400 cursor-pointer hover:text-blue-600"
                                onClick={() => {
                                    const d = new Date(currentDate);
                                    d.setMonth(d.getMonth() + 1);
                                    setCurrentDate(d);
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-7 text-center text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-tighter">
                        {t('calendar.dayLetters').map((d, i) => <div key={i}>{d}</div>)}
                    </div>
                    {/* Simplified mini-grid for context */}
                    <div className="grid grid-cols-7 gap-y-1 text-center text-xs">
                        {Array.from({ length: 31 }, (_, i) => {
                            const isToday = new Date().getDate() === i + 1 && new Date().getMonth() === currentDate.getMonth();
                            return (
                                <div key={i + 1} className={`py-1.5 cursor-pointer rounded-full transition-colors ${isToday ? 'bg-blue-600 text-white font-bold' : 'hover:bg-slate-50 text-slate-600'}`}>
                                    {i + 1}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-auto space-y-4">
                    <Card className="bg-[#0f172a] text-white overflow-hidden border-0">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-400 rounded-2xl flex items-center justify-center text-[#0f172a]">
                                <Check className="h-6 w-6 stroke-[3px]" />
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">75%</p>
                                <h3 className="text-sm font-bold">Configuração</h3>
                            </div>
                            <ChevronRight className="h-4 w-4 text-slate-500" />
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Main Calendar View */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header Toolbar */}
                <div className="h-16 border-b border-slate-100 flex items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <div className="hidden md:flex items-center gap-2 text-blue-600 font-medium text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded-lg">
                            <Users className="h-4 w-4" /> <span>{t('calendar.allSpecialists')}</span> <ChevronDown className="h-4 w-4" />
                        </div>
                        <div className="flex bg-slate-100 rounded-lg p-1">
                            <button
                                onClick={() => setCurrentDate(new Date())}
                                className="px-4 py-1.5 rounded-md text-xs font-bold text-slate-600 hover:bg-white shadow-sm transition-all"
                            >
                                {t('common.today')}
                            </button>
                        </div>
                        <div className="flex items-center gap-4 ml-2">
                            <ChevronLeft
                                className="h-5 w-5 text-slate-400 cursor-pointer hover:text-blue-600"
                                onClick={() => {
                                    const d = new Date(currentDate);
                                    d.setDate(d.getDate() - 7);
                                    setCurrentDate(d);
                                }}
                            />
                            <span className="font-bold text-slate-900 min-w-[100px] text-center">
                                {currentDate.toLocaleString(language,{ month: 'short', year: 'numeric' })}
                            </span>
                            <ChevronRight
                                className="h-5 w-5 text-slate-400 cursor-pointer hover:text-blue-600"
                                onClick={() => {
                                    const d = new Date(currentDate);
                                    d.setDate(d.getDate() + 7);
                                    setCurrentDate(d);
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden lg:flex bg-slate-100 rounded-lg p-1">
                            <button className="px-4 py-1.5 rounded-md text-xs font-bold text-slate-600">{t('common.day')}</button>
                            <button className="px-4 py-1.5 rounded-md bg-white shadow-sm text-xs font-bold text-slate-900">{t('common.week')}</button>
                        </div>
                        <Button variant="outline" size="sm" className="hidden sm:flex gap-2 text-blue-600 border-slate-200">
                            <Download className="h-4 w-4" /> {t('common.export')}
                        </Button>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3">
                            <Plus className="h-5 w-5" />
                        </Button>
                    </div>
                </div>

                {/* Weekly Grid */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex border-b border-slate-100 overflow-y-scroll invisible-scrollbar">
                        <div className="w-16 border-r border-slate-50 shrink-0" />
                        {weekDates.map((date, i) => {
                            const isToday = new Date().toDateString() === date.toDateString();
                            return (
                                <div key={i} className="flex-1 py-4 text-center border-r border-slate-50 last:border-r-0">
                                    <div className={`text-[10px] uppercase font-bold mb-1 ${isToday ? 'text-blue-600' : 'text-slate-400'}`}>
                                        {dayNames[i]}
                                    </div>
                                    <div className={`text-xl font-bold inline-flex w-10 h-10 items-center justify-center rounded-full transition-colors ${isToday ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-slate-900'}`}>
                                        {date.getDate()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <div className="flex min-h-full">
                            {/* Time Labels */}
                            <div className="w-16 border-r border-slate-50 shrink-0 bg-white sticky left-0 z-20">
                                {Array.from({ length: 15 }).map((_, i) => (
                                    <div key={i} className="h-20 border-b border-slate-50 flex items-start justify-end pr-3 pt-2 text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                        {8 + i}:00
                                    </div>
                                ))}
                            </div>

                            {/* Grid and Appointments */}
                            {weekDates.map((date, dayIdx) => (
                                <div key={dayIdx} className="flex-1 border-r border-slate-50 last:border-r-0 relative group hover:bg-slate-50/30 transition-colors">
                                    {/* Grid Lines */}
                                    {Array.from({ length: 15 }).map((_, i) => (
                                        <div key={i} className="h-20 border-b border-slate-50" />
                                    ))}

                                    {/* Real Appointments */}
                                    {getAppointmentsForDay(date).map((app) => {
                                        const appDate = new Date(app.appointment_date);
                                        const startHour = appDate.getHours();
                                        const startMin = appDate.getMinutes();
                                        const top = (startHour - 8) * 80 + (startMin / 60) * 80;

                                        return (
                                            <div
                                                key={app.id}
                                                className="absolute left-1 right-1 p-0.5 z-10"
                                                style={{ top: `${top}px`, height: '70px' }}
                                            >
                                                <Card className="h-full bg-blue-50 border-blue-200 shadow-sm hover:shadow-md transition-all cursor-pointer border-l-4 border-l-blue-600">
                                                    <div className="p-2 overflow-hidden">
                                                        <div className="flex justify-between items-center mb-0.5">
                                                            <span className="text-[10px] font-bold text-blue-800 truncate">
                                                                {app.contact_id || t('calendar.client')}
                                                            </span>
                                                            <span className="text-[9px] font-medium text-blue-600">
                                                                {appDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        <p className="text-[9px] text-slate-600 truncate font-medium">
                                                            {app.services?.name || t('calendar.service')}
                                                        </p>
                                                        <div className="mt-1 flex items-center gap-1">
                                                            <div className={`w-1.5 h-1.5 rounded-full ${app.status === 'confirmed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                                            <span className="text-[8px] uppercase font-black text-slate-400 tracking-tighter">{app.status === 'confirmed' ? t('common.confirmed') : app.status === 'pending' ? t('common.pending') : app.status}</span>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Config Check (Bottom Left) */}
            <div className="absolute bottom-6 left-6 w-72 bg-[#1e293b] rounded-2xl p-4 shadow-2xl z-30">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                        <Check className="h-4 w-4 text-emerald-500" />
                    </div>
                    <span className="text-white text-xs font-bold">{t('calendar.setupComplete')}</span>
                </div>
                <div className="h-1 w-full bg-slate-700 rounded-full overflow-hidden mb-4">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: '75%' }} />
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed mb-4">{t('calendar.setupReminder')}</p>
                <Button variant="secondary" className="w-full h-8 text-[10px] font-bold bg-white/10 hover:bg-white/20 text-white border-0">
                    {t('calendar.completeSetup')}
                </Button>
            </div>
        </div>
    );
};

const ChevronDown = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

export default CalendarPage;
